import { useQuery } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { observer } from 'mobx-react-lite';
import {
  type ReactNode,
  useDeferredValue,
  useEffect,
  useRef,
  useState,
  useTransition,
} from 'react';

import { Icons } from '@/assets/icons/icons';
import { MainLayoutHeaderTunnel } from '@/components/blocks/MainLayout/main-layout-tunnel';
import { ModalsManager } from '@/components/modals/modalsManager';
import { Text } from '@/components/ui';
import { Button } from '@/components/ui/Button';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Empty } from '@/components/ui/EmptyState';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { ScrollMask } from '@/components/ui/ScrollMask';
import { Skeleton } from '@/components/ui/skeleton';
import { Markdown } from '@/components/ui/smoothdown';
import { Page } from '@/design-system-v2/page';
import { RoundButton } from '@/design-system-v2/round-button';
import { RoundSearchInput } from '@/design-system-v2/round-search-input';
import { CodingAgentsPanel } from '@/features/coding-agents';
import { MCPAddServerModal } from '@/features/mcp/MCPAddServerModal';
import { useHashRoute } from '@/hooks/useHashRoute';
import { hasIntegrationAccess } from '@/integrations/integrations.utils';
import { cn } from '@/lib/utils';
import { trackEvent } from '@/utils/analytics';
import { handleExternalUrlClick } from '@/utils/platform';

import { copyFile, fileName } from './copy-file';
import { PluginsIntegrations } from './plugins-integrations';
import {
  filterSkills,
  getVfsFileQuery,
  hydrateSkillForm,
  type Skill,
  SKILL_FILE_NAME,
  trySkillPrompt,
  CREATE_SKILL_PROMPT,
  useSkillsQuery,
  VfsFileNotFoundError,
} from './plugins-query';
import { PLUGINS_TAB_LABELS, pluginsStore } from './plugins.store';
import { SkillCard } from './skill-card';
import { skillIcon } from './skill-icon';
import { TeamSkillRowControls } from './team-skill-actions';

const SKILLS_DOCS_URL = 'https://support.littlebird.ai/docs/skills/';
const MESSAGE_STYLE = 'py-6 text-foreground-muted text-xs';
const SECTION_LABEL = 'text-xs font-medium text-foreground-muted';
const PROBLEM_MESSAGES: Record<string, string> = {
  missing_skill_file: "This skill's file is missing",
  invalid_frontmatter: "This skill's header is incomplete or unreadable",
  invalid_name: "This skill's name isn't formatted properly",
  name_mismatch: "The name in the header doesn't match the folder",
};

const problemMessage = (skill: Skill) =>
  PROBLEM_MESSAGES[skill.problem ?? ''] ??
  "Littlebird can't use this skill as-is";

function SkillRow({ skill, onSelect }: { skill: Skill; onSelect: () => void }) {
  const SkillIcon = skillIcon(skill);
  return (
    <XStack className="no-record items-center rounded-[0.875rem] border border-border bg-surface transition-colors hover:bg-surface-hover">
      <button
        type="button"
        onClick={onSelect}
        className="flex min-w-0 flex-1 items-center gap-3.5 px-4 py-3.5 text-left focus-ring"
      >
        <span className="inline-flex size-5 shrink-0 items-center justify-center text-foreground-muted">
          <SkillIcon className="size-5" strokeWidth={1.5} />
        </span>
        <YStack className="min-w-0 flex-1 gap-0">
          <span className="truncate text-sm.5 leading-6 font-medium text-foreground">
            {skill.label}
          </span>
          <span className="truncate text-xs.5 leading-5 text-foreground-muted">
            {skill.isBroken ? problemMessage(skill) : skill.description}
          </span>
          {skill.teamSkill && (
            <Text className="truncate text-xs text-foreground-subtle">
              Shared by {skill.teamSkill.shared_by ?? 'a former teammate'}
            </Text>
          )}
        </YStack>
      </button>
      {skill.teamSkill && <TeamSkillRowControls team={skill.teamSkill} />}
    </XStack>
  );
}

function SkillGroup({
  title,
  action,
  skills,
  onSelect,
}: {
  title: string;
  action?: ReactNode;
  skills: Skill[];
  onSelect: (skill: Skill) => void;
}) {
  if (skills.length === 0) {
    return null;
  }
  return (
    <YStack className="gap-2">
      <XStack className="items-center justify-between">
        <span className="px-0.5 text-sm font-medium text-foreground-muted">
          {title}
        </span>
        {action}
      </XStack>
      {skills.map((skill) => (
        <SkillRow
          key={skill.path}
          skill={skill}
          onSelect={() => onSelect(skill)}
        />
      ))}
    </YStack>
  );
}

function BackToSkills({ onBack }: { onBack: () => void }) {
  return (
    <XStack>
      <button
        type="button"
        onClick={onBack}
        className="flex items-center gap-1.5 rounded text-sm text-foreground-muted transition-colors hover:text-foreground focus-ring"
      >
        <Icons.arrowLeft className="size-3.5" />
        Skills
      </button>
    </XStack>
  );
}

function SkillFiles({
  skill,
  viewedPath,
  onView,
}: {
  skill: Skill;
  viewedPath: string;
  onView: (path: string) => void;
}) {
  if (skill.files.length === 0) return null;
  return (
    <YStack className="gap-1 border-t border-border pt-3">
      <span className={SECTION_LABEL}>Files</span>
      {[skill.path, ...skill.files].map((path) => (
        <button
          key={path}
          type="button"
          onClick={() => onView(path)}
          className={cn(
            'w-fit rounded font-mono text-xs leading-5 transition-colors hover:text-foreground focus-ring',
            path === viewedPath ? 'text-foreground' : 'text-foreground-muted'
          )}
        >
          {path.slice(skill.dir.length + 1)}
        </button>
      ))}
    </YStack>
  );
}

/** A bundled file other than SKILL.md, read-only */
function BundledFileView({
  skill,
  path,
  onView,
}: {
  skill: Skill;
  path: string;
  onView: (path: string) => void;
}) {
  const { data: file, isLoading, error } = useQuery(getVfsFileQuery(path));
  return (
    <XStack className="items-start gap-5">
      <YStack className="no-record min-w-0 flex-1 gap-4 rounded-lg border border-border bg-surface px-5 py-4">
        <span className="font-serif text-lg">{skill.label}</span>
        {isLoading ? (
          <div className={MESSAGE_STYLE}>Loading...</div>
        ) : error || !file ? (
          <div className={MESSAGE_STYLE}>
            {error instanceof VfsFileNotFoundError
              ? 'This file is no longer available.'
              : 'Could not load this file. Try again in a moment.'}
          </div>
        ) : path.endsWith('.md') ? (
          <div className="prose prose-bird max-w-none select-text">
            <Markdown
              id={path}
              content={file.text}
              className="prose-sm [&_h1]:!text-base [&_h2]:!text-sm [&_h3]:!text-sm [&_h4]:!text-xs"
            />
          </div>
        ) : (
          <pre className="overflow-x-auto rounded-md border border-border bg-secondary p-2.5 font-mono text-xs leading-4 text-foreground select-text">
            {file.text}
          </pre>
        )}
        <SkillFiles skill={skill} viewedPath={path} onView={onView} />
      </YStack>
      <YStack className="w-52 shrink-0 gap-2">
        {file && !file.truncated && (
          <Button
            variant="secondary"
            size="sm"
            className="grid-cols-[minmax(0,1fr)] [&>span]:max-w-full"
            onClick={() => void copyFile(path, file.text)}
          >
            <span className="min-w-0 truncate">Copy {fileName(path)}</span>
          </Button>
        )}
      </YStack>
    </XStack>
  );
}

function SkillDetail({
  skill,
  findTaken,
  onBack,
  onSaved,
}: {
  skill: Skill;
  findTaken: (name: string) => Skill | undefined;
  onBack: () => void;
  onSaved: (dir: string) => void;
}) {
  const navigate = useNavigate();
  const [viewedPath, setViewedPath] = useState(skill.path);
  const {
    data: file,
    isPending,
    isError,
    error,
  } = useQuery(getVfsFileQuery(skill.path));
  const isMissingFile = error instanceof VfsFileNotFoundError;

  if (viewedPath !== skill.path) {
    return (
      <YStack className="gap-3">
        <BackToSkills onBack={onBack} />
        <BundledFileView
          skill={skill}
          path={viewedPath}
          onView={setViewedPath}
        />
      </YStack>
    );
  }

  return (
    <YStack className="gap-3">
      <BackToSkills onBack={onBack} />
      {isPending ? (
        <Skeleton className="h-48 rounded-lg" />
      ) : isError && !file && !isMissingFile ? (
        <div className={MESSAGE_STYLE}>
          Could not load this skill. Try again in a moment.
        </div>
      ) : (
        <SkillCard
          skill={skill}
          problem={skill.isBroken ? problemMessage(skill) : undefined}
          initial={hydrateSkillForm(skill, file?.text)}
          isTruncated={!!file?.truncated}
          editing={!skill.isSystem && !skill.isTeam}
          findTaken={(name) =>
            name === skill.name ? undefined : findTaken(name)
          }
          onSaved={onSaved}
          onTry={() =>
            void navigate({
              to: '/chats',
              search: { auto_prompt: trySkillPrompt(skill) },
            })
          }
          onCopy={
            file && !file.truncated
              ? () => void copyFile(skill.path, file.text)
              : undefined
          }
          footer={
            <SkillFiles
              skill={skill}
              viewedPath={viewedPath}
              onView={setViewedPath}
            />
          }
        />
      )}
    </YStack>
  );
}

function NewSkill({
  findTaken,
  onBack,
  onSaved,
}: {
  findTaken: (name: string) => Skill | undefined;
  onBack: () => void;
  onSaved: (dir: string) => void;
}) {
  return (
    <YStack className="gap-3">
      <BackToSkills onBack={onBack} />
      <SkillCard
        initial={hydrateSkillForm(undefined, undefined)}
        isTruncated={false}
        editing
        findTaken={findTaken}
        onSaved={onSaved}
      />
    </YStack>
  );
}

function PluginSearchInput({
  onQueryChange,
  placeholder,
}: {
  onQueryChange: (query: string) => void;
  placeholder: string;
}) {
  const [query, setQuery] = useState('');
  const [, startTransition] = useTransition();
  const inputRef = useRef<HTMLElement | undefined>(undefined);
  useEffect(() => {
    requestAnimationFrame(() => inputRef.current?.focus());
  }, []);

  const updateQuery = (query: string) => {
    setQuery(query);
    startTransition(() => onQueryChange(query));
  };

  return (
    <XStack className="relative h-9 flex-1 items-center gap-2 rounded-lg border border-border bg-surface pl-3 pr-1">
      <Icons.search
        aria-hidden="true"
        className="size-3.5 shrink-0 text-foreground-muted"
      />
      <input
        ref={(input) => {
          inputRef.current = input ?? undefined;
        }}
        type="text"
        aria-label={placeholder}
        placeholder={placeholder}
        value={query}
        onChange={(event) => updateQuery(event.target.value)}
        className="h-full w-full bg-transparent pr-8 text-sm outline-none placeholder:text-foreground-muted"
      />
      {query && (
        <button
          type="button"
          onClick={() => updateQuery('')}
          aria-label="Clear search"
          className="absolute right-2 inline-flex size-6 shrink-0 items-center justify-center rounded-md text-foreground-muted hover:bg-secondary-hover hover:text-foreground"
        >
          <Icons.cancel className="size-3.5" />
        </button>
      )}
    </XStack>
  );
}

export const PluginsPage = observer(function PluginsPage() {
  const activeTab = pluginsStore.activeTab;
  const [selectedPath, setSelectedPath] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [skillQuery, setSkillQuery] = useState('');
  const [integrationQuery, setIntegrationQuery] = useState('');
  // Filtering the grid lags behind typing so the input stays responsive
  const deferredIntegrationQuery = useDeferredValue(integrationQuery);
  const navigate = useNavigate();
  const { setHash } = useHashRoute();
  const { systemSkills, teamSkills, userSkills, isLoading, error } =
    useSkillsQuery();

  // Held by path, not by object, so the detail follows edits and closes on delete
  const selectedSkill =
    userSkills.find((skill) => skill.path === selectedPath) ??
    teamSkills.find((skill) => skill.path === selectedPath) ??
    systemSkills.find((skill) => skill.path === selectedPath) ??
    null;

  const createWithLittlebird = () =>
    void navigate({
      to: '/chats',
      search: { auto_prompt: CREATE_SKILL_PROMPT },
    });
  const setUpManually = () => setCreating(true);
  const addCustomIntegration = () => {
    if (!hasIntegrationAccess()) {
      trackEvent('UPGRADE_BUTTON_CLICKED', {
        source: 'plugins_add_custom_button',
      });
      setHash('settings/Subscription');
      return;
    }
    ModalsManager.show(MCPAddServerModal, {});
  };
  const selectSaved = (dir: string) => {
    setCreating(false);
    setSelectedPath(`${dir}/${SKILL_FILE_NAME}`);
  };
  const findTaken = (name: string) =>
    userSkills.find((existing) => existing.name === name) ??
    systemSkills.find((existing) => existing.name === name);
  const filteredUserSkills = filterSkills(userSkills, skillQuery);
  const filteredTeamSkills = filterSkills(teamSkills, skillQuery);
  const filteredSystemSkills = filterSkills(systemSkills, skillQuery);
  const isSearching = skillQuery.trim().length > 0;
  const showNoMatches =
    isSearching &&
    !error &&
    filteredUserSkills.length === 0 &&
    filteredTeamSkills.length === 0 &&
    filteredSystemSkills.length === 0;

  return (
    <YStack className="h-full min-h-0 overflow-hidden">
      <Page.Column width="full" className={cn('shrink-0 gap-6', 'pt-1')}>
        <MainLayoutHeaderTunnel.In>
          <Page.Title>{PLUGINS_TAB_LABELS[activeTab]}</Page.Title>
        </MainLayoutHeaderTunnel.In>
        {null}
      </Page.Column>
      {activeTab === 'skills' && creating ? (
        <ScrollMask className="min-h-0 flex-1 pt-6 pb-6 scrollbar-hide">
          <Page.Column width="full">
            <NewSkill
              findTaken={findTaken}
              onBack={() => setCreating(false)}
              onSaved={selectSaved}
            />
          </Page.Column>
        </ScrollMask>
      ) : activeTab === 'skills' && selectedSkill ? (
        <ScrollMask className="min-h-0 flex-1 pt-6 pb-6 scrollbar-hide">
          <Page.Column width="full">
            <SkillDetail
              key={selectedSkill.path}
              skill={selectedSkill}
              findTaken={findTaken}
              onBack={() => setSelectedPath(null)}
              onSaved={selectSaved}
            />
          </Page.Column>
        </ScrollMask>
      ) : activeTab === 'coding-agents' ? (
        <ScrollMask className="min-h-0 flex-1 pt-6 pb-6 scrollbar-hide">
          <Page.Column width="full">
            <CodingAgentsPanel />
          </Page.Column>
        </ScrollMask>
      ) : activeTab === 'integrations' ? (
        <PluginsIntegrations
          query={deferredIntegrationQuery}
          filter="all"
          toolbar={
            <Page.HeaderActions>
              <RoundSearchInput
                value={integrationQuery}
                onChange={setIntegrationQuery}
                placeholder="Search integrations..."
                aria-label="Search integrations"
                className="w-80 min-w-0 flex-initial"
                autoFocus
              />
              <RoundButton icon={Icons.plus} onClick={addCustomIntegration}>
                Add custom
              </RoundButton>
            </Page.HeaderActions>
          }
        />
      ) : (
        <>
          <XStack
            className={cn(
              Page.column({ width: 'full' }),
              'shrink-0 items-center gap-3 pt-6'
            )}
          >
            <PluginSearchInput
              onQueryChange={setSkillQuery}
              placeholder="Search skills..."
            />
            <DropdownMenu.Root>
              <DropdownMenu.Trigger asChild>
                <Button
                  variant="default"
                  className="h-9 shrink-0 gap-1.5 focus-visible:outline-none focus-visible:ring-0 data-[state=open]:bg-accent data-[state=open]:text-accent-foreground-subtle"
                >
                  <Icons.plus className="size-3.5" />
                  New skill
                  <Icons.chevronDown className="size-3.5" />
                </Button>
              </DropdownMenu.Trigger>
              <DropdownMenu.Content
                align="end"
                className="min-w-48"
                fit
                onCloseAutoFocus={(event) => event.preventDefault()}
              >
                <DropdownMenu.Item onSelect={createWithLittlebird}>
                  <Icons.newChat />
                  Create with Littlebird
                </DropdownMenu.Item>
                <DropdownMenu.Item onSelect={setUpManually}>
                  <Icons.slidersHorizontal />
                  Set up manually
                </DropdownMenu.Item>
              </DropdownMenu.Content>
            </DropdownMenu.Root>
          </XStack>
          <ScrollMask className="min-h-0 flex-1 pt-6 pb-6 scrollbar-hide">
            <Page.Column width="full" className="gap-6">
              {isLoading ? (
                <YStack className="gap-2">
                  {[...Array(4)].map((_, index) => (
                    <Skeleton key={index} className="h-[4.375rem] rounded-xl" />
                  ))}
                </YStack>
              ) : showNoMatches ? (
                <Empty.Root>
                  <Empty.Media variant="icon">
                    <Icons.search />
                  </Empty.Media>
                  <Empty.Header>
                    <Empty.Title>No skills found</Empty.Title>
                    <Empty.Description>
                      Try adjusting your search.
                    </Empty.Description>
                  </Empty.Header>
                </Empty.Root>
              ) : error ? (
                <div className={MESSAGE_STYLE}>
                  Could not load skills. Try again in a moment.
                </div>
              ) : (
                <>
                  {userSkills.length === 0 && !isSearching ? (
                    <Empty.Root variant="action">
                      <Empty.Header>
                        <Empty.Title>No skills yet</Empty.Title>
                        <Empty.Description>
                          Create one here, or ask Littlebird in chat.
                        </Empty.Description>
                      </Empty.Header>
                      <Empty.Actions>
                        <Button
                          variant="secondary"
                          className="gap-2"
                          onClick={createWithLittlebird}
                        >
                          <Icons.newChat className="size-4" />
                          Create with Littlebird
                        </Button>
                        <Button
                          variant="ghost"
                          className="gap-2"
                          onClick={setUpManually}
                        >
                          <Icons.slidersHorizontal className="size-4" />
                          Set up manually
                        </Button>
                      </Empty.Actions>
                    </Empty.Root>
                  ) : (
                    <SkillGroup
                      title="Your skills"
                      action={
                        <button
                          type="button"
                          onClick={() =>
                            handleExternalUrlClick(SKILLS_DOCS_URL)
                          }
                          className="cursor-pointer px-0.5 text-sm text-foreground-muted underline underline-offset-4 transition-colors hover:text-foreground"
                        >
                          Read more
                        </button>
                      }
                      skills={filteredUserSkills}
                      onSelect={(skill) => setSelectedPath(skill.path)}
                    />
                  )}
                  <SkillGroup
                    title="Team skills"
                    skills={filteredTeamSkills}
                    onSelect={(skill) => setSelectedPath(skill.path)}
                  />
                  <SkillGroup
                    title="Built-in (internal only)"
                    skills={filteredSystemSkills}
                    onSelect={(skill) => setSelectedPath(skill.path)}
                  />
                </>
              )}
            </Page.Column>
          </ScrollMask>
        </>
      )}
    </YStack>
  );
});
